import React, { type ReactNode, Children, isValidElement } from "react";

/**
 * Checks if React children contain meaningful content (not empty fragments)
 *
 * This utility helps determine whether to render container elements when
 * children might be empty fragments, null, or other falsy values.
 *
 * @param children - React children to check
 * @returns true if children contain meaningful content, false otherwise
 *
 * @example
 * ```tsx
 * // Returns false
 * hasMeaningfulChildren(null)
 * hasMeaningfulChildren(undefined)
 * hasMeaningfulChildren(<></>)
 * hasMeaningfulChildren(<>{null}</>)
 *
 * // Returns true
 * hasMeaningfulChildren(<div>Content</div>)
 * hasMeaningfulChildren("Text content")
 * hasMeaningfulChildren(<><div>Content</div></>)
 * ```
 */
export function hasMeaningfulChildren(children: ReactNode): boolean {
  if (!children) return false;

  const childrenArray = Children.toArray(children);

  // If no children, return false
  if (childrenArray.length === 0) return false;

  // Check each child
  for (const child of childrenArray) {
    // Skip null, undefined, boolean, number 0, empty string
    if (child == null || child === 0 || child === "") {
      continue;
    }

    // If it's a React element, check if it's a fragment
    if (isValidElement(child)) {
      // If it's a fragment, recursively check its children
      if (child.type === React.Fragment) {
        const fragmentChildren = (child.props as { children?: ReactNode })
          .children;
        if (hasMeaningfulChildren(fragmentChildren)) {
          return true;
        }
        continue;
      }
      // If it's any other element, it's meaningful
      return true;
    }

    // If it's a string with content, it's meaningful
    if (typeof child === "string" && child.trim() !== "") {
      return true;
    }

    // If it's a number (not 0), it's meaningful
    if (typeof child === "number" && child !== 0) {
      return true;
    }
  }

  return false;
}
